import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { StatusBadge } from './SchemaListPage';
import type { FieldValue, ImportResult, ParticipantDoc, SchemaDoc, SchemaProperty } from '../types';

type FieldDraft = Record<string, FieldValue>;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid var(--border-color)',
  borderRadius: 4,
  background: 'var(--card-bg)',
  color: 'var(--text-color)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
};

function emptyDraft(schema: SchemaDoc): FieldDraft {
  const draft: FieldDraft = {};
  for (const p of schema.properties) {
    draft[p.name] = p.type === 'boolean' ? false : '';
  }
  return draft;
}

export function ParticipantsPage() {
  const { industryId, schemaId } = useParams<{ industryId: string; schemaId: string }>();
  const [schema, setSchema] = useState<SchemaDoc | null>(null);
  const [participants, setParticipants] = useState<ParticipantDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [humanReadableId, setHumanReadableId] = useState('');
  const [draft, setDraft] = useState<FieldDraft>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [importing, setImporting] = useState(false);
  const [importCsv, setImportCsv] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  useEffect(() => {
    if (!industryId || !schemaId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [s, plist] = await Promise.all([
          apiCall<SchemaDoc>(`/api/schemas/${schemaId}?industryId=${encodeURIComponent(industryId)}`),
          apiCall<ParticipantDoc[]>(
            `/api/schemas/${schemaId}/participants?industryId=${encodeURIComponent(industryId)}`,
          ),
        ]);
        if (cancelled) return;
        setSchema(s);
        setParticipants(plist);
        setDraft(emptyDraft(s));
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [industryId, schemaId]);

  function setField(name: string, value: FieldValue) {
    setDraft((prev) => ({ ...prev, [name]: value }));
  }

  function resetForm() {
    if (schema) setDraft(emptyDraft(schema));
    setHumanReadableId('');
    setFormError(null);
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!schema || !industryId || !schemaId) return;
    for (const p of schema.properties) {
      if (p.required) {
        const v = draft[p.name];
        if (v === '' || v === null || v === undefined) {
          setFormError(`"${p.label}" is required.`);
          return;
        }
      }
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const fields: Record<string, FieldValue> = {};
      for (const p of schema.properties) {
        const raw = draft[p.name] ?? null;
        if (p.type === 'number') {
          fields[p.name] = raw === '' || raw === null ? null : Number(raw);
        } else if (p.type === 'boolean') {
          fields[p.name] = Boolean(raw);
        } else {
          fields[p.name] = raw === '' ? null : raw;
        }
      }
      const created = await apiCall<ParticipantDoc>('/api/participants', {
        method: 'POST',
        body: {
          industryId,
          schemaId,
          humanReadableId: humanReadableId.trim() || undefined,
          fields,
        },
      });
      setParticipants((prev) => [created, ...prev]);
      setAdding(false);
      resetForm();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function deactivate(id: string) {
    if (!industryId) return;
    if (!window.confirm('Deactivate this participant? Their active QR codes will no longer resolve.')) return;
    try {
      const updated = await apiCall<ParticipantDoc>(
        `/api/participants/${id}/deactivate?industryId=${encodeURIComponent(industryId)}`,
        { method: 'POST' },
      );
      setParticipants((prev) => prev.map((p) => (p.id === id ? updated : p)));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  async function handleFileChosen(file: File) {
    const text = await file.text();
    setImportCsv(text);
  }

  async function runImport() {
    if (!industryId || !schemaId || !importCsv.trim()) return;
    setImportBusy(true);
    setImportError(null);
    setImportResult(null);
    try {
      const res = await apiCall<ImportResult>(
        `/api/participants/import?industryId=${encodeURIComponent(industryId)}&schemaId=${encodeURIComponent(
          schemaId,
        )}`,
        { method: 'POST', body: { csv: importCsv } },
      );
      setImportResult(res);
      // Refresh participant list.
      const plist = await apiCall<ParticipantDoc[]>(
        `/api/schemas/${schemaId}/participants?industryId=${encodeURIComponent(industryId)}`,
        { skipCache: true },
      );
      setParticipants(plist);
    } catch (e) {
      setImportError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setImportBusy(false);
    }
  }

  if (!industryId || !schemaId) {
    return <p style={{ color: 'var(--danger-color)' }}>Missing route parameters.</p>;
  }

  // Show the first 4 visible columns (skip ones marked PII to be safer in lists,
  // but still show low-sensitivity fields).
  const visibleColumns: SchemaProperty[] =
    schema?.properties.filter((p) => !p.pii).slice(0, 4) ??
    schema?.properties.slice(0, 4) ??
    [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <Link
            to={`/industries/${industryId}/schemas`}
            style={{ color: 'var(--text-muted)', fontSize: 13, textDecoration: 'none' }}
          >
            ← Schemas
          </Link>
          <h1 style={{ margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 12 }}>
            Participants{schema ? ` · ${schema.name}` : ''}
            {schema && <StatusBadge status={schema.status} />}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!adding && schema && (
            <button className="btn btn--primary" onClick={() => setAdding(true)}>
              + Add participant
            </button>
          )}
          {schema && (
            <button className="btn" onClick={() => setImporting(true)}>
              Import CSV
            </button>
          )}
          <Link
            to={`/industries/${industryId}/schemas/${schemaId}/batch`}
            className="btn"
            style={{ textDecoration: 'none' }}
          >
            Generate QR for all
          </Link>
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fee', border: '1px solid #fcc', borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {adding && schema && (
        <form
          onSubmit={submit}
          style={{
            border: '1px solid var(--border-color)',
            background: 'var(--card-bg)',
            padding: 20,
            borderRadius: 8,
            marginBottom: 24,
          }}
        >
          <h3 style={{ marginTop: 0 }}>New participant</h3>
          {formError && (
            <div
              style={{
                padding: 12,
                background: '#fee',
                border: '1px solid #fcc',
                borderRadius: 6,
                marginBottom: 16,
              }}
            >
              {formError}
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Human-readable ID</label>
            <input
              value={humanReadableId}
              onChange={(e) => setHumanReadableId(e.target.value)}
              autoFocus
              placeholder="(auto-generated if left blank)"
              style={inputStyle}
            />
          </div>
          {schema.properties.map((p) => (
            <div key={p.name} style={{ marginBottom: 12 }}>
              <label style={labelStyle}>
                {p.label}
                {p.required ? ' *' : ''}
                {p.pii ? (
                  <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--warning-color, #d97706)' }}>
                    PII
                  </span>
                ) : null}
              </label>
              <FieldInput property={p} value={draft[p.name]} onChange={(v) => setField(p.name, v)} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Create participant'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setAdding(false);
                resetForm();
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {importing && (
        <div
          className="modal-overlay"
          onClick={() => {
            setImporting(false);
            setImportCsv('');
            setImportResult(null);
            setImportError(null);
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Import participants from CSV</h2>
              <button
                className="btn btn--ghost"
                aria-label="Close"
                onClick={() => {
                  setImporting(false);
                  setImportCsv('');
                  setImportResult(null);
                  setImportError(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                Paste CSV content directly or upload a file. The first row must contain the field
                machine names.
              </p>
              <div>
                <label style={labelStyle}>Upload file</label>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleFileChosen(f);
                  }}
                />
              </div>
              <div>
                <label style={labelStyle}>CSV</label>
                <textarea
                  value={importCsv}
                  onChange={(e) => setImportCsv(e.target.value)}
                  rows={10}
                  style={{ ...inputStyle, fontFamily: 'monospace' }}
                  placeholder="first_name,last_name,date_of_birth\nAda,Lovelace,1815-12-10"
                />
              </div>
              {importError && <div className="form-error">{importError}</div>}
              {importResult && (
                <div
                  style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: 6,
                    padding: 12,
                    background: 'var(--bg-color, #fafafa)',
                  }}
                >
                  <strong>Imported {importResult.created}</strong>
                  {importResult.skipped > 0 && ` · skipped ${importResult.skipped}`}
                  {importResult.errors.length > 0 && (
                    <>
                      <p style={{ marginTop: 8, marginBottom: 4 }}>Errors:</p>
                      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                        {importResult.errors.map((er, i) => (
                          <li key={i}>
                            Row {er.row}: {er.message}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn--ghost"
                onClick={() => {
                  setImporting(false);
                  setImportCsv('');
                  setImportResult(null);
                  setImportError(null);
                }}
              >
                Close
              </button>
              <button
                className="btn btn--primary"
                onClick={() => void runImport()}
                disabled={importBusy || !importCsv.trim()}
              >
                {importBusy ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : !schema ? null : participants.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>
          No participants yet. Add one to start generating QR codes.
        </p>
      ) : (
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: 8,
            background: 'var(--card-bg)',
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
                <th style={{ padding: 12 }}>ID</th>
                {visibleColumns.map((p) => (
                  <th key={p.name} style={{ padding: 12 }}>
                    {p.label}
                  </th>
                ))}
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}>Source</th>
                <th style={{ padding: 12 }}>Created</th>
                <th style={{ padding: 12 }}></th>
              </tr>
            </thead>
            <tbody>
              {participants.map((part) => (
                <tr key={part.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                  <td style={{ padding: 12, fontWeight: 500 }}>{part.humanReadableId ?? part.id.slice(0, 8)}</td>
                  {visibleColumns.map((p) => (
                    <td key={p.name} style={{ padding: 12, color: 'var(--text-muted)' }}>
                      {formatFieldValue(part.fields[p.name])}
                    </td>
                  ))}
                  <td style={{ padding: 12 }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 999,
                        textTransform: 'uppercase',
                        background:
                          part.status === 'active' ? 'var(--success-color, #10b981)' : 'var(--border-color)',
                        color: part.status === 'active' ? '#fff' : 'var(--text-muted)',
                      }}
                    >
                      {part.status}
                    </span>
                  </td>
                  <td style={{ padding: 12, color: 'var(--text-muted)', fontSize: 12 }}>
                    {part.createdVia}
                  </td>
                  <td style={{ padding: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(part.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: 12, whiteSpace: 'nowrap', display: 'flex', gap: 6 }}>
                    <Link
                      to={`/industries/${industryId}/schemas/${schemaId}/generate?participantId=${encodeURIComponent(part.id)}`}
                      className="btn btn--ghost"
                      style={{ textDecoration: 'none' }}
                    >
                      Generate QR
                    </Link>
                    {part.status === 'active' && (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => void deactivate(part.id)}
                        style={{ color: 'var(--danger-color)' }}
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatFieldValue(v: FieldValue | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function FieldInput({
  property,
  value,
  onChange,
}: {
  property: SchemaProperty;
  value: FieldValue | undefined;
  onChange: (v: FieldValue) => void;
}) {
  switch (property.type) {
    case 'number':
      return (
        <input
          type="number"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
    case 'boolean':
      return (
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      );
    case 'select':
      return (
        <select
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        >
          <option value="">— Select —</option>
          {(property.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    default:
      return (
        <input
          type="text"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
  }
}
